<template>
  <v-container fluid grid-list-xl>
    <v-layout row wrap ma-0 mb-2 pa-0>
      <v-flex pt-0>
        <v-layout>
          <template>
            <h2><span class="grey--text">{{ paymentType === 'ach' ? 'ACH' : 'Card' }}</span> Installment Transactions</h2>
            <Lightbulb medium>
              <template v-slot:message>
                <span>This screen will allow you to search for installment transactions using the custom search button. The Option button next to any transaction allows you to manage the installment schedule.</span>
              </template>
            </Lightbulb>
          </template>
        </v-layout>
      </v-flex>
    </v-layout>

    <div>
      <v-data-table
        :headers="columnsDisplayed"
        :items="rows"
        :pagination.sync="pagination"
        :loading="loading"
        :total-items="totalRows"
        :rows-per-page-items="pageCountOptions"
        item-key="id"
        class="elevation-1"
        must-sort
        no-data-text="No data found"
        no-results-text="No results found"
        @update:pagination="search"
      >
        <template slot="headers" slot-scope="props">
          <tr>
            <th
              v-for="(header) in props.headers"
              :key="header.text"
              :class="['font-weight-bold text-xs-left column sortable pa-1 ma-0', pagination.descending ? 'desc' : 'asc', header.value === pagination.sortBy ? 'active' : '']"
              @click="changeSort(header.value)"
            >
              {{ header.text }}
              <v-icon small>arrow_upward</v-icon>
            </th>
            <th class="ma-0 pa-0 text-xs-right">
              <v-btn icon flat color="primary" @click="reset">
                <v-icon>fa-refresh</v-icon>
              </v-btn>
            </th>
          </tr>
        </template>

        <template slot="items" slot-scope="props">
          <tr>
            <td
              v-for="(header) in columnsDisplayed"
              :key="header.value"
              v-show="header.visible"
              class="pa-1 ma-0"
            >
              <template v-if="header.value === 'total_amount'">
                {{ moneyNoSign(props.item.total_amount / 100) }}
              </template>

              <template v-else-if="header.value === 'net_amount' || header.value === 'less_fee_amount'">
                {{ moneyNoSign((props.item.total_amount - props.item.fee_amount) / 100) }}
              </template>

              <template v-else-if="header.value === 'card_expiration_yymm'">
                {{ expiration(props.item.card_expiration_yymm) }}
              </template>

              <template v-else-if="header.value === 'ach_is_authorized'">
                <span class="font-weight-bold red--text" v-if="!props.item.ach_is_authorized">No</span>
                <span v-else>Yes</span>
              </template>

              <template v-else-if="header.type === 'boolean'">
                <span v-if="props.item[header.value]">Yes</span>
                <span v-else>No</span>
              </template>

              <template v-else-if="header.type === 'money'">
                {{ moneyNoSign(props.item[header.value] / 100) }}
              </template>

              <template v-else-if="header.value === 'next_run'">
                {{ (props.item.recurring_status !== 'ended' && props.item.recurring_status !== 'cancelled') ? dateUtc(props.item[header.value]) : '' }}
              </template>

              <template v-else-if="header.type === 'date'">
                {{ dateUtc(props.item[header.value]) }}
              </template>

              <template v-else>
                <span class="text-capitalize">{{ props.item[header.value] }}</span>
              </template>
            </td>
            <td class="ma-0 pa-0 text-xs-right">
              <v-btn small v-if="props.item.id" :to="{ name: 'PnxInstallmentManage', query: { id: props.item.id }}">Options</v-btn>
            </td>
          </tr>
        </template>

        <template slot="no-data">
          No results found
        </template>
      </v-data-table>

      <v-layout row wrap justify-end>
        <v-btn color="primary" @click="searchModal = true">
          Search
          <v-divider vertical class="ml-2 mr-2" v-if="hasPermission('has_inline_help')"></v-divider>
          <Lightbulb color="primary--text">
            <template v-slot:message>
              <span>Use the fields or buttons below to search specific transactions</span>
            </template>
          </Lightbulb>
        </v-btn>
        <v-btn class="mb-2" @click="exportFull">Export</v-btn>
      </v-layout>
    </div>

    <v-dialog scrollable v-model="searchModal" @submit.prevent @keydown.esc.prevent="searchModal=false">
      <v-card>
        <v-toolbar dense flat>
          <v-toolbar-title>
            Search {{ paymentType === 'ach' ? 'ACH' : 'Card' }} Installments
            <Lightbulb medium>
              <template v-slot:message>
                <span>This screen will allow you to search for installment schedules.</span>
              </template>
            </Lightbulb>
          </v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn icon flat color="primary" @click.native="searchModal = false">
            <v-icon>fa-close</v-icon>
          </v-btn>
        </v-toolbar>

        <v-divider class="primary"></v-divider>

        <v-card-text>
          <v-form @keyup.native.enter="searchNew">
            <v-container fluid pa-0>
              <v-layout row wrap>
                <v-flex xs6 sm3>
                  <v-text-field
                    label="Customer Name"
                    v-model="filter.customer_name"
                    box
                    hide-details
                  ></v-text-field>
                </v-flex>
                <v-flex xs6 sm3>
                  <v-text-field
                    label="Last Four"
                    v-model="filter.last_four"
                    box
                    hide-details
                  ></v-text-field>
                </v-flex>
                <v-flex xs6 sm3>
                  <v-text-field
                    label="Amount"
                    v-model="filter.amount"
                    box
                    hide-details
                  ></v-text-field>
                </v-flex>
                <v-flex xs6 sm3>
                  <v-text-field
                    label="Transaction ID"
                    v-model="filter.tracking_id"
                    box
                    hide-details
                  ></v-text-field>
                </v-flex>

                <v-flex xs6 sm3>
                  <v-menu
                    ref="startDateModal"
                    :close-on-content-click="false"
                    v-model="startDateModal"
                    :nudge-right="40"
                    :return-value.sync="filter.start_date"
                    lazy
                    transition="scale-transition"
                    offset-y
                    full-width
                    min-width="290px"
                  >
                    <v-text-field
                      slot="activator"
                      v-model="formattedStartDate"
                      label="Start"
                      append-icon="event"
                      box
                      hide-details
                    ></v-text-field>
                    <v-date-picker
                      v-model="filter.start_date"
                      no-title
                      @input="$refs.startDateModal.save(filter.start_date)"
                    ></v-date-picker>
                  </v-menu>
                  <v-menu
                    ref="endDateModal"
                    :close-on-content-click="false"
                    v-model="endDateModal"
                    :nudge-right="40"
                    :return-value.sync="filter.end_date"
                    lazy
                    transition="scale-transition"
                    offset-y
                    full-width
                    min-width="290px"
                  >
                    <v-text-field
                      slot="activator"
                      v-model="formattedEndDate"
                      label="End"
                      append-icon="event"
                      box
                      hide-details
                    ></v-text-field>
                    <v-date-picker
                      v-model="filter.end_date"
                      no-title
                      @input="$refs.endDateModal.save(filter.end_date)"
                    ></v-date-picker>
                  </v-menu>
                </v-flex>

                <v-flex xs6 sm3>
                  <v-select
                    name="recurring_status"
                    label="Status"
                    v-model="filter.recurring_status"
                    :items="statuses"
                    multiple
                    box
                    hide-details
                  ></v-select>
                </v-flex>

                <v-flex xs6 sm3>
                  <v-select
                    name="recurring_type"
                    label="Frequency"
                    v-model="filter.recurring_type"
                    :items="recurringTypes"
                    multiple
                    box
                    hide-details
                  ></v-select>
                </v-flex>

                <v-flex xs6 sm3>
                  <v-text-field
                    label="Customer PO"
                    v-model="filter.customer_po"
                    box
                    hide-details
                  ></v-text-field>
                </v-flex>
                <v-flex xs6 sm3>
                  <v-text-field
                    label="Custom 1"
                    v-model="filter.custom_field_1"
                    box
                    hide-details
                  ></v-text-field>
                </v-flex>
                <v-flex xs6 sm3>
                  <v-text-field
                    label="Custom 2"
                    v-model="filter.custom_field_2"
                    box
                    hide-details
                  ></v-text-field>
                </v-flex>
                <v-flex xs6 sm3>
                  <v-text-field
                    label="Custom 3"
                    v-model="filter.custom_field_3"
                    box
                    hide-details
                  ></v-text-field>
                </v-flex>
                <v-flex xs6 sm3>
                  <v-text-field
                    label="Notes"
                    v-model="filter.notes"
                    box
                    hide-details
                  ></v-text-field>
                </v-flex>
                <v-flex xs6 sm3 v-if="paymentType === 'ach' && terminalsACH.length > 1">
                  <v-autocomplete
                    name="terminal"
                    label="Terminal"
                    v-model="filter.terminal_id"
                    :items="terminals"
                    item-text="name"
                    item-value="terminal_id"
                    box
                    hide-details
                  ></v-autocomplete>
                </v-flex>
                <v-flex xs6 sm3 v-if="paymentType !== 'ach' && terminalsCard.length > 1">
                  <v-autocomplete
                    name="terminal"
                    label="Terminal"
                    v-model="filter.terminal_id"
                    :items="terminals"
                    item-text="name"
                    item-value="terminal_id"
                    box
                    hide-details
                  ></v-autocomplete>
                </v-flex>
                <v-flex xs6 sm3>
                  <v-autocomplete
                    name="account"
                    label="Account"
                    v-model="filter.account_id"
                    :items="accounts"
                    item-text="name"
                    item-value="account_id"
                    box
                    hide-details
                    v-if="myProfile.accounts.length > 1"
                  ></v-autocomplete>
                </v-flex>
              </v-layout>

              <v-divider class="primary"></v-divider>

              <v-layout row wrap>
                <v-spacer></v-spacer>
                <v-flex>
                  <v-layout row wrap justify-end>
                    <v-btn color="primary" @click="searchNew">Search</v-btn>
                    <v-btn @click="reset">Clear</v-btn>
                  </v-layout>
                </v-flex>
              </v-layout>
            </v-container>
          </v-form>
        </v-card-text>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import Moment from 'moment'

import Bus from '@/bus'
import ApiMixin from '@/mixins/api'
import AuthMixin from '@/mixins/auth'
import DisplayMixin from '@/mixins/display'
import ExportMixin from '@/mixins/export'
import RecurringMixin from '@/mixins/recurring'
import StoreMixin from '@/store/mixin'

import Lightbulb from '@/components/help/Lightbulb'

export default {
  mixins: [
    ApiMixin,
    AuthMixin,
    DisplayMixin,
    ExportMixin,
    RecurringMixin,
    StoreMixin
  ],

  components: {Lightbulb},

  data: function () {
    return {
      statuses: [
        {value: 'cancelled', text: 'Cancelled'},
        {value: 'declined', text: 'Declined'},
        {value: 'ended', text: 'Ended'},
        {value: 'pending', text: 'Pending'},
        {value: 'running', text: 'Running'}
      ],

      columns: this.defaultColumns(),
      pagination: this.defaultPagination(),
      rows: [],
      totalRows: 0,
      pageCountOptions: [5, 10, 25, 50, 100],
      loading: true,

      searchModal: false,
      startDateModal: false,
      endDateModal: false,

      filter: this.defaultFilter()
    }
  },

  props: {
    paymentType: String
  },

  created () {
    Bus.$on('chargeSuccessful', this.search)
    Bus.$on('optionsClose', this.search)
  },

  beforeDestroy () {
    Bus.$off('chargeSuccessful', this.search)
    Bus.$off('optionsClose', this.search)
  },

  destroyed () {
    this.$store.commit('clearErrors')
    this.$store.commit('clearInfo')
  },

  computed: {
    recurringTypes () {
      return this.getRecurringTypes()
    },

    accounts: function () {
      let accounts = [
        {account_id: '*', name: 'All'}
      ]
      return accounts.concat(this.myProfile.accounts)
    },
    terminals: function () {
      let terminals = [
        {terminal_id: '*', name: 'All'}
      ]
      if (this.paymentType === 'ach') {
        return terminals.concat(this.terminalsACH)
      } else {
        return terminals.concat(this.terminalsCard)
      }
    },

    columnsDisplayed: function () {
      return this.columns.map(column => {
        let visible = column.visible
        if (column.value === 'ach_is_authorized') {
          visible = this.paymentType === 'ach'
        }
        if (column.value === 'card_expiration_yymm' && this.paymentType === 'ach') {
          visible = false
        }
        if (column.value === 'terminal_code') {
          if (this.paymentType === 'ach' && this.terminalsACH.length < 2) visible = false
          if (this.paymentType !== 'ach' && this.terminalsCard.length < 2) visible = false
        }
        if (column.value === 'account_name' && this.myProfile.accounts.length < 2) {
          visible = false
        }
        return { ...column, visible }
      }).filter(column => column.visible)
    },

    columnsExported: function () {
      return this.columns.map(column => {
        let exp = column.export
        if (column.value === 'ach_is_authorized') {
          exp = this.paymentType === 'ach'
        }
        if (column.value === 'card_expiration_yymm' && this.paymentType === 'ach') {
          exp = false
        }
        if (column.value === 'terminal_code') {
          if (this.paymentType === 'ach' && this.terminalsACH.length < 2) exp = false
          if (this.paymentType !== 'ach' && this.terminalsCard.length < 2) exp = false
        }
        if (column.value === 'account_name' && this.myProfile.accounts.length < 2) {
          exp = false
        }
        return { ...column, export: exp }
      }).filter(column => column.export)
    },

    formattedStartDate () {
      return this.filter.start_date ? Moment(this.filter.start_date).format('MMM-DD-YYYY') : ''
    },
    formattedEndDate () {
      return this.filter.end_date ? Moment(this.filter.end_date).format('MMM-DD-YYYY') : ''
    }
  },

  watch: {
    $route (to, from) {
      this.reset()
    }
  },

  methods: {
    defaultColumns () {
      return [
        {
          value: 'card_name',
          text: 'Cust. Name',
          sortable: true,
          visible: true,
          export: true
        },
        {
          value: 'last_four',
          text: 'Last Four',
          sortable: true,
          visible: true,
          export: true
        },
        {
          value: 'card_expiration_yymm',
          text: 'Expiration',
          sortable: true,
          visible: true,
          export: true
        },
        {
          value: 'ach_is_authorized',
          text: 'Authorized',
          sortable: true,
          visible: false,
          export: false,
          type: 'boolean'
        },
        {
          value: 'total_amount',
          text: 'Amount ($)',
          sortable: true,
          visible: true,
          export: true,
          type: 'money'
        },
        {
          value: 'recurring_type',
          text: 'Frequency',
          sortable: true,
          visible: true,
          export: true
        },
        {
          value: 'start',
          text: 'Start',
          sortable: true,
          visible: true,
          export: true,
          type: 'date'
        },
        {
          value: 'end',
          text: 'End',
          sortable: true,
          visible: true,
          export: true,
          type: 'date'
        },
        {
          value: 'next_run',
          text: 'Next Run',
          sortable: true,
          visible: true,
          export: true,
          type: 'date'
        },
        {
          value: 'recurring_status',
          text: 'Status',
          sortable: true,
          visible: true,
          export: true
        },
        {
          value: 'terminal_code',
          text: 'Terminal',
          sortable: true,
          visible: true,
          export: true
        },
        {
          value: 'account_name',
          text: 'Account',
          sortable: true,
          visible: true,
          export: true
        }
      ]
    },

    defaultFilter () {
      let basefilter = {
        account_id: '*',
        amount: '',
        customer_name: '',
        start_date: '',
        end_date: '',
        recurring_type: ['daily', 'weekly', 'biweekly', 'triweekly', 'monthly', 'bimonthly', 'quarterly', 'semiannually', 'annually'],
        is_installment: '1',
        last_four: '',
        terminal_id: '*',
        transaction_id: '',
        tracking_id: '',
        recurring_status: ['cancelled', 'declined', 'ended', 'pending', 'running'],
        customer_po: '',
        custom_field_1: '',
        custom_field_2: '',
        custom_field_3: '',
        notes: ''
      }

      if (this.paymentType === 'ach') {
        return {
          ...basefilter,
          request_type: 'ach',
          ach_is_authorized: '*'
        }
      } else {
        return {
          ...basefilter,
          request_type: 'sale'
        }
      }
    },

    defaultPagination () {
      return {
        sortBy: 'schedules.created',
        descending: this.$store.state.paginationDefaults.descending,
        rowsPerPage: this.$store.state.paginationDefaults.rowsPerPage,
        page: 1,
        totalItems: 0
      }
    },

    searchNew () {
      this.pagination.page = 1
      this.search()
    },

    async search () {
      this.loading = true

      try {
        let filter = { ...this.filter }

        if (filter.start_date) {
          filter.start_date = Moment(this.filter.start_date).utc().format('YYYY-MM-DD HH:mm:ss')
        }
        if (filter.end_date) {
          filter.end_date = Moment(Moment(this.filter.end_date).format('YYYY-MM-DD') + ' 23:59:59').utc().format('YYYY-MM-DD HH:mm:ss')
        }

        let result = await this.api({
          method: 'get',
          url: '/schedules',
          params: {
            filter: filter,
            page: this.pagination.page - 1,
            perPage: this.pagination.rowsPerPage,
            orderBy: this.pagination.sortBy,
            orderDirection: this.pagination.descending ? 'desc' : 'asc'
          }
        })
        this.rows = result.data.rows
        this.totalRows = result.data.count
        this.pagination.totalItems = result.data.count
      } catch (err) {
        Bus.$emit('openModal', {
          status: 'error',
          message: err
        })
      } finally {
        this.searchModal = false
        this.loading = false
      }
    },

    async exportFull () {
      this.loading = true

      try {
        let filter = { ...this.filter }

        if (filter.start_date) {
          filter.start_date = Moment(this.filter.start_date).utc().format('YYYY-MM-DD HH:mm:ss')
        }
        if (filter.end_date) {
          filter.end_date = Moment(Moment(this.filter.end_date).format('YYYY-MM-DD') + ' 23:59:59').utc().format('YYYY-MM-DD HH:mm:ss')
        }

        let result = await this.api({
          method: 'get',
          url: '/schedules',
          params: {
            filter: filter,
            perPage: '*',
            orderBy: this.pagination.sortBy,
            orderDirection: this.pagination.descending ? 'desc' : 'asc'
          }
        })

        let csvContent = 'data:text/csv;charset=utf-8,'

        csvContent += [
          this.columnsExported.map(column => { return '"' + column.text + '"' }).join(','),
          ...result.data.rows.map(row => {
            return this.columnsExported.map(column => {
              let value = row[column.value]

              if (value === null) {
                value = ''
              }
              if (column.type === 'boolean') {
                value = (value === 0) ? 'No' : 'Yes'
              }
              if (column.type === 'money') {
                value = '$' + (value / 100).toFixed(2)
              }
              if (column.value === 'card_expiration_yymm') {
                value = this.expiration(value)
              }
              if (column.value === 'recurring_status' || column.value === 'recurring_type') {
                value = value.charAt(0).toUpperCase() + value.slice(1)
              }

              return '"' + ('' + value).replace(/"/g, '""') + '"'
            }).join(',')
          })
        ]
          .join('\n')

        await this.downloadCSVData(csvContent)
      } catch (err) {
        Bus.$emit('openModal', {
          status: 'error',
          message: err
        })
      } finally {
        this.loading = false
      }
    },

    reset () {
      this.columns = this.defaultColumns()
      this.filter = this.defaultFilter()
      this.pagination = this.defaultPagination()

      this.search()
    },

    changeSort (column) {
      if (this.pagination.sortBy === column) {
        this.pagination.descending = !this.pagination.descending
      } else {
        this.pagination.sortBy = column
        this.pagination.descending = false
      }
      this.search()
    }
  }
}
</script>



// WEBPACK FOOTER //
// src/components/PnxInstallments.vue